import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * CrashClawConnectorPanel — 连接器配置子模块
 *
 * 把原 MCP Server + Channels 两个 Tab 合并为统一的"连接器"概念，
 * 用户不再需要区分 MCP / Channel，按"数据源 / 协作工具 / 通知通道 / 扩展"四种用途接入即可。
 *
 * 数据源：
 *   - GET POST /redir/api/crashclaw/listConnectors → 返回预定义连接器（含 iWiki / Knot）及当前状态
 *
 * 接入方式：
 *   - 工蜂 Git / TAPD / iWiki / Knot ：OAuth 快捷授权（新窗口 + 前端轮询 listConnectors）
 *   - 企业微信 Bot          ：扫码 Modal（二维码 + 每 2s 轮询企微授权结果）
 *   - 邮件 / Webhook        ：弹原生 Modal 表单提交
 *   - 自定义 MCP Server     ：弹 inline 自定义 MCP 表单，调真后端 createMcpServer 接口
 *
 * 默认连接：
 *   - CrashSight MCP        ：创建工作区时由 ensureCrashSightMcp 自动配置，本面板仅展示状态
 *
 * 不在此面板的能力：
 *   - 删除现有 MCP / 修改协议参数 → 复杂场景仍保留在原 CrashClawMcpPanel（本期已从壳里摘掉，
 *     文件保留以便回滚；后续可视情况补"高级配置"入口）
 */
import { useEffect, useState, useCallback } from 'react';
import { Button, Form, Input, message, Modal, Radio, Tabs, Tooltip } from 'antd';
import { ApiOutlined, CheckCircleFilled, CloudOutlined, EditOutlined, ExclamationCircleFilled, LinkOutlined, LoadingOutlined, MailOutlined, PlusOutlined, QrcodeOutlined, ReloadOutlined, WechatOutlined, } from '@ant-design/icons';
import { ze } from 'utils/zhEn';
import RestHelper from 'utils/RestHelper';
import { isCrashSightMcpUrl } from './useQuickAddCrashSightMcp';
import giteeLogo from './assets/gitee.png';
import tapdLogo from './assets/tapd.png';
import iwikiLogo from './assets/iwiki.png';
import knotLogo from './assets/knot.png';
import wecomLogo from './assets/wecom.png';
import style from './style.scss';
/**
 * 品牌 logo 渲染：外层圆角浅灰底（44x44）由 .connectorIcon 提供，内部图按 size 居中显示。
 * 不同品牌图本身留白差异较大（工蜂图边距小，TAPD/企微图边距大），通过 size 微调让视觉占比一致。
 */
const BrandLogo = ({ src, alt, size = 26 }) => (_jsx("img", { src: src, alt: alt, style: { width: size, height: size, objectFit: 'contain' } }));
// ======================== Constants ========================
const API_PREFIX = '/redir/api/crashclaw';
/** 预定义连接器在 server_config.connectorKey 中的标识，需从自定义 MCP 列表中排除 */
const PREDEFINED_CONNECTOR_KEYS = new Set([
    'gitee_git', 'tapd', 'iwiki', 'knot', 'wecom_bot', 'email', 'webhook', 'crashsight_mcp',
]);
/** 本期暂不展示的通知通道卡片（后端能力保留，仅隐藏入口） */
const HIDDEN_CONNECTOR_KEYS = new Set(['email', 'webhook']);
/** 判断 listMcpServers 中的条目是否为用户手动添加的自定义 MCP */
function isUserCustomMcp(mcp) {
    if (mcp.transportType === 'CONNECTOR')
        return false;
    const connectorKey = mcp.serverConfig?.connectorKey;
    if (typeof connectorKey === 'string' && PREDEFINED_CONNECTOR_KEYS.has(connectorKey)) {
        return false;
    }
    if (mcp.serverConfig?.oauthProvider === 'taihu')
        return false;
    const url = mcp.serverConfig?.url;
    if (typeof url === 'string' && isCrashSightMcpUrl(url))
        return false;
    return true;
}
function buildCustomMcpMeta(mcp) {
    const transportLabel = mcp.transportType.replace('_', ' ');
    return {
        key: `mcp:${mcp.mcpUid}`,
        mcpUid: mcp.mcpUid,
        mcpEnabled: mcp.enabled,
        icon: _jsx(ApiOutlined, { style: { color: '#1677ff' } }),
        title: mcp.serverName,
        subtitle: mcp.description || ze(`${transportLabel} MCP Server`, `${transportLabel} MCP Server`),
        category: 'quick',
        mode: 'custom-mcp',
        actionText: '',
        typeLabel: 'MCP',
        tagColor: 'geekblue',
    };
}
/**
 * 卡片元数据（不包含运行时状态，状态从 listConnectors 拉）。
 * key 必须与 mock-gateway 里的 connector key 一致。
 */
const CONNECTOR_META = [
    {
        key: 'crashsight_mcp',
        icon: _jsx(CloudOutlined, { style: { color: '#ff7d00' } }),
        title: 'CrashSight MCP',
        subtitle: ze('崩溃数据访问 - 创建工作区时自动配置', 'Crash data access - auto-configured on workspace creation'),
        category: 'default',
        mode: 'auto',
        actionText: '',
        typeLabel: ze('数据源', 'Data Source'),
        tagColor: 'orange',
    },
    {
        key: 'gitee_git',
        icon: _jsx(BrandLogo, { src: giteeLogo, alt: "Gitee", size: 22 }),
        title: ze('工蜂 Git', 'Gitee Git'),
        subtitle: ze('访问工蜂仓库代码与提交记录', 'Access Gitee repositories and commits'),
        category: 'quick',
        mode: 'oauth',
        actionText: ze('快捷授权', 'Quick Authorize'),
        typeLabel: ze('数据源', 'Data Source'),
        tagColor: 'red',
    },
    {
        key: 'tapd',
        icon: _jsx(BrandLogo, { src: tapdLogo, alt: "TAPD", size: 36 }),
        title: 'TAPD',
        subtitle: ze('授权后可自动创建 Bug 单与需求关联', 'Authorize to auto-create bugs and link stories'),
        category: 'quick',
        mode: 'oauth',
        actionText: ze('快捷授权', 'Quick Authorize'),
        typeLabel: ze('协作工具', 'Collaboration'),
        tagColor: 'blue',
    },
    {
        key: 'iwiki',
        icon: _jsx(BrandLogo, { src: iwikiLogo, alt: "iWiki", size: 28 }),
        title: 'iWiki',
        subtitle: ze('iWiki 的 MCP 服务，为大模型赋予文档创建、检索等能力：支持创建 iWiki 文档、Markdown 文档，能够通过检索找出相关联的文档', 'iWiki MCP: create/search documents and retrieve related wiki pages for the model'),
        category: 'quick',
        mode: 'oauth',
        actionText: ze('快捷授权', 'Quick Authorize'),
        typeLabel: ze('文档协作', 'Docs'),
        tagColor: 'geekblue',
    },
    {
        key: 'knot',
        icon: _jsx(BrandLogo, { src: knotLogo, alt: "Knot", size: 28 }),
        title: 'Knot',
        subtitle: ze('Knot 的 MCP 服务，授权后可访问 Knot 智能体、Skill 与知识库能力', 'Knot MCP: access Knot agents, skills and knowledge bases after authorization'),
        category: 'quick',
        mode: 'oauth',
        actionText: ze('快捷授权', 'Quick Authorize'),
        typeLabel: ze('智能体平台', 'Agent Platform'),
        tagColor: 'purple',
    },
    {
        key: 'wecom_bot',
        icon: _jsx(BrandLogo, { src: wecomLogo, alt: "WeCom", size: 36 }),
        title: ze('企业微信', 'WeCom'),
        subtitle: ze('创建企微智能机器人，向团队主动推送崩溃告警与摘要', 'Create a WeCom intelligent bot to push crash alerts and summaries'),
        category: 'quick',
        mode: 'qrcode',
        actionText: ze('接入', 'Connect'),
        typeLabel: ze('通知通道', 'Notification'),
        tagColor: 'green',
    },
    {
        key: 'email',
        icon: _jsx(MailOutlined, { style: { color: '#722ed1' } }),
        title: ze('邮件通知', 'Email'),
        subtitle: ze('配置接收告警的邮箱列表', 'Configure recipient email list'),
        category: 'quick',
        mode: 'form',
        actionText: ze('填写邮箱', 'Configure'),
        typeLabel: ze('通知通道', 'Notification'),
        tagColor: 'purple',
    },
    {
        key: 'webhook',
        icon: _jsx(LinkOutlined, { style: { color: '#13c2c2' } }),
        title: ze('自定义 Webhook', 'Custom Webhook'),
        subtitle: ze('支持自定义推送目标 URL', 'Push to a custom URL'),
        category: 'quick',
        mode: 'form',
        actionText: ze('填写 URL', 'Configure'),
        typeLabel: ze('通知通道', 'Notification'),
        tagColor: 'cyan',
    },
];
// ======================== Component ========================
const CrashClawConnectorPanel = () => {
    const [connectors, setConnectors] = useState([]);
    const [customMcpMetas, setCustomMcpMetas] = useState([]);
    const [loading, setLoading] = useState(false);
    // 当前正在等待 OAuth 授权回调的连接器（用于 loading 态）
    const [oauthPolling, setOauthPolling] = useState(new Set());
    // 企微接入 Modal —— 双 Tab：扫码绑定 / 手动填写。
    // 实际二维码渲染在子 Modal 内（用户点"去绑定"后才弹），父 Modal 仅作 Tab 切换与说明展示。
    const [wecomBindVisible, setWecomBindVisible] = useState(false);
    const [wecomBindTab, setWecomBindTab] = useState('qrcode');
    const [wecomManualForm] = Form.useForm();
    const [wecomManualSubmitting, setWecomManualSubmitting] = useState(false);
    // 二维码子 Modal —— 点"去绑定"后展示真二维码 + 启动轮询
    const [wecomQrModalVisible, setWecomQrModalVisible] = useState(false);
    const [wecomQrcode, setWecomQrcode] = useState('');
    const [wecomTicket, setWecomTicket] = useState('');
    const [wecomStatus, setWecomStatus] = useState('pending');
    // 邮件 / Webhook 通用表单 Modal
    const [formModalVisible, setFormModalVisible] = useState(false);
    const [formModalTarget, setFormModalTarget] = useState('');
    const [form] = Form.useForm();
    // 自定义 MCP Modal（与原 MCP 模块「添加 MCP Server」弹窗保持一致：Radio.Button 切换 +
    // JSON Editor + 测试连接按钮 + 测试结果展示）
    const [customMcpVisible, setCustomMcpVisible] = useState(false);
    const [customMcpSubmitting, setCustomMcpSubmitting] = useState(false);
    const [customMcpForm] = Form.useForm();
    const [customMcpTestLoading, setCustomMcpTestLoading] = useState(false);
    const [customMcpTestResult, setCustomMcpTestResult] = useState(null);
    // -------------------- Data fetching --------------------
    const fetchConnectors = useCallback(async (silent = false) => {
        if (!silent)
            setLoading(true);
        try {
            const [connRsp, mcpRsp] = await Promise.all([
                RestHelper.mustPost(`${API_PREFIX}/listConnectors`),
                RestHelper.mustPost(`${API_PREFIX}/listMcpServers`),
            ]);
            const list = connRsp.json.data || [];
            setConnectors(list);
            const customMcps = (mcpRsp.json.data || []).filter(isUserCustomMcp);
            setCustomMcpMetas(customMcps.map(buildCustomMcpMeta));
            return list;
        }
        catch (e) {
            console.error('Failed to list connectors', e);
            return null;
        }
        finally {
            if (!silent)
                setLoading(false);
        }
    }, []);
    useEffect(() => {
        fetchConnectors();
    }, [fetchConnectors]);
    /** 约每分钟静默刷新连接器状态（OAuth token / 健康探测） */
    useEffect(() => {
        const timer = window.setInterval(() => {
            fetchConnectors(true);
        }, 60_000);
        return () => window.clearInterval(timer);
    }, [fetchConnectors]);
    function getStatus(key) {
        return connectors.find(c => c.connectorKey === key)?.status || 'disconnected';
    }
    // -------------------- OAuth 快捷授权 --------------------
    /**
     * 工蜂/TAPD/iWiki/Knot：调 oauthAuthorize 拿太湖 OAuth redirectUrl，新窗口打开供用户授权；
     * 同时每 2s 轮询 listConnectors（用接口返回值判断，避免 setInterval 闭包读到旧 state），
     * 发现授权完成即停止。最长轮询 120s。
     */
    async function handleOAuth(connectorKey) {
        // 点击瞬间的状态：重新授权时已是 connected，不能仅靠 status 判定成功
        const alreadyConnected = getStatus(connectorKey) === 'connected';
        setOauthPolling(prev => new Set(prev).add(connectorKey));
        const stopPolling = () => {
            setOauthPolling(prev => {
                const next = new Set(prev);
                next.delete(connectorKey);
                return next;
            });
        };
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/oauthAuthorize`, { connectorKey });
            const win = window.open(rsp.json.data.redirectUrl, '_blank', 'width=600,height=700');
            if (!win) {
                message.warning(ze('浏览器拦截了新窗口，请允许弹窗后重试', 'Browser blocked the popup, please allow popups and retry'));
            }
            message.info(ze('授权后页面会自动刷新连接状态', 'Connector status will refresh automatically after authorization'));
            let pollCount = 0;
            const timer = setInterval(async () => {
                pollCount += 1;
                const list = await fetchConnectors(true);
                const connected = !!list?.some(c => c.connectorKey === connectorKey && c.status === 'connected');
                // 首次授权：status 变为 connected 即成功。
                // 重新授权：点击时已是 connected，需等授权窗口关闭后再确认，避免 2s 假成功。
                const authDone = alreadyConnected
                    ? connected && !!win && win.closed
                    : connected;
                if (authDone) {
                    clearInterval(timer);
                    stopPolling();
                    message.success(alreadyConnected
                        ? ze('连接状态已刷新', 'Connection status refreshed')
                        : ze('授权成功', 'Authorization succeeded'));
                }
                else if (pollCount >= 60) {
                    clearInterval(timer);
                    stopPolling();
                    message.info(ze('授权超时，如已完成授权请点击刷新按钮', 'Authorization timed out. If you already authorized, click Refresh'));
                }
            }, 2000);
        }
        catch (e) {
            console.error('OAuth authorize failed', e);
            message.error(ze('授权请求失败，请稍后重试', 'Authorization request failed, please retry'));
            stopPolling();
        }
    }
    // -------------------- 企微接入：双 Tab Modal --------------------
    /** 打开企微接入 Modal（双 Tab） */
    function openWecomBindModal() {
        setWecomBindVisible(true);
        setWecomBindTab('qrcode');
        wecomManualForm.resetFields();
    }
    /**
     * 用户在「扫码绑定」Tab 点"去绑定"按钮：
     * 调 generateWecomQrcode 拿真二维码 → 弹出二维码子 Modal → 启动每 2s 轮询。
     * 注意：父 Modal 不关闭，方便用户失败后切回手动填写 Tab。
     */
    async function handleStartWecomQrcodeBind() {
        setWecomStatus('pending');
        setWecomQrcode('');
        setWecomTicket('');
        setWecomQrModalVisible(true);
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/generateWecomQrcode`);
            setWecomQrcode(rsp.json.data.qrcode);
            setWecomTicket(rsp.json.data.ticket);
        }
        catch (e) {
            console.error('Generate WeCom qrcode failed', e);
            message.error(ze('二维码生成失败', 'Failed to generate QR code'));
            setWecomQrModalVisible(false);
        }
    }
    /**
     * 用户在「手动填写」Tab 提交 Bot ID / Secret：
     * 调 bindWecomManual 接口，后端写入 BotId/Secret 并同步到 Channel。
     */
    async function handleSubmitWecomManual() {
        try {
            const values = await wecomManualForm.validateFields();
            setWecomManualSubmitting(true);
            await RestHelper.mustPost(`${API_PREFIX}/bindWecomManual`, {
                botId: values.botId,
                secret: values.secret,
            });
            message.success(ze('已保存，机器人将在 5-8s 后生效', 'Saved. Bot will be active in 5-8s'));
            setWecomBindVisible(false);
            fetchConnectors(true);
        }
        catch (e) {
            if (e && typeof e === 'object' && 'errorFields' in e)
                return;
            console.error('Bind WeCom manually failed', e);
            message.error(ze('保存失败', 'Save failed'));
        }
        finally {
            setWecomManualSubmitting(false);
        }
    }
    // 扫码二维码轮询：只在二维码子 Modal 打开 + 有 ticket + 未成功时跑
    useEffect(() => {
        if (!wecomQrModalVisible || !wecomTicket || wecomStatus === 'success')
            return undefined;
        const timer = setInterval(async () => {
            try {
                const rsp = await RestHelper.mustPost(`${API_PREFIX}/pollWecomScan?ticket=${encodeURIComponent(wecomTicket)}`);
                if (rsp.json.data.status === 'success') {
                    setWecomStatus('success');
                    message.success(ze('企微智能机器人创建成功', 'WeCom intelligent bot created successfully'));
                    fetchConnectors(true);
                    // 成功后关掉二维码子 Modal 与父 Modal
                    setTimeout(() => {
                        setWecomQrModalVisible(false);
                        setWecomBindVisible(false);
                    }, 1500);
                }
            }
            catch (e) {
                console.error('Poll WeCom scan failed', e);
            }
        }, 2000);
        return () => clearInterval(timer);
    }, [wecomQrModalVisible, wecomTicket, wecomStatus, fetchConnectors]);
    // -------------------- 邮件 / Webhook 通用表单 --------------------
    function openFormModal(connectorKey) {
        form.resetFields();
        setFormModalTarget(connectorKey);
        setFormModalVisible(true);
    }
    async function handleFormSubmit() {
        try {
            const values = await form.validateFields();
            await RestHelper.mustPost(`${API_PREFIX}/configureConnector`, {
                connectorKey: formModalTarget,
                config: values,
            });
            message.success(ze('配置已保存', 'Configuration saved'));
            setFormModalVisible(false);
            fetchConnectors(true);
        }
        catch (e) {
            if (e && typeof e === 'object' && 'errorFields' in e)
                return;
            console.error('Configure connector failed', e);
            message.error(ze('保存失败', 'Save failed'));
        }
    }
    // -------------------- 自定义 MCP Server --------------------
    function openCustomMcpModal() {
        customMcpForm.resetFields();
        setCustomMcpTestResult(null);
        setCustomMcpVisible(true);
    }
    function closeCustomMcpModal() {
        setCustomMcpVisible(false);
        setCustomMcpTestResult(null);
        setCustomMcpTestLoading(false);
    }
    /** 复用原 MCP Panel 的测试连接 API：传 transportType + 解析后的 JSON，后端跑一次连通 */
    async function handleCustomMcpTestConnection() {
        try {
            const values = await customMcpForm.validateFields();
            if (values.transportType === 'STDIO') {
                message.info(ze('STDIO 类型不支持远程连通性测试', 'STDIO transport does not support remote connectivity testing'));
                return;
            }
            let serverConfig = {};
            try {
                serverConfig = JSON.parse(values.serverConfig || '{}');
            }
            catch {
                message.error(ze('服务配置 JSON 格式不合法，请检查', 'Invalid JSON in server config, please check'));
                return;
            }
            setCustomMcpTestLoading(true);
            setCustomMcpTestResult(null);
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/testMcpServer`, {
                transportType: values.transportType,
                serverConfig,
            });
            setCustomMcpTestResult(rsp.json.data);
        }
        catch (e) {
            if (e && typeof e === 'object' && 'errorFields' in e)
                return;
            console.error('testMcpServer by config failed', e);
            setCustomMcpTestResult({
                success: false,
                errorMessage: e instanceof Error ? e.message : String(e),
                latencyMs: 0,
            });
        }
        finally {
            setCustomMcpTestLoading(false);
        }
    }
    async function handleCustomMcpSubmit() {
        try {
            const values = await customMcpForm.validateFields();
            let serverConfig = {};
            try {
                serverConfig = JSON.parse(values.serverConfig);
            }
            catch {
                message.error(ze('服务配置 JSON 格式不合法', 'Invalid JSON in server config'));
                return;
            }
            setCustomMcpSubmitting(true);
            await RestHelper.mustPost(`${API_PREFIX}/createMcpServer`, {
                serverName: values.serverName,
                transportType: values.transportType,
                serverConfig,
                description: values.description || null,
            });
            message.success(ze('自定义 MCP 已添加', 'Custom MCP added'));
            closeCustomMcpModal();
            fetchConnectors(true);
        }
        catch (e) {
            if (e && typeof e === 'object' && 'errorFields' in e)
                return;
            console.error('Create custom MCP failed', e);
            message.error(ze('添加失败', 'Add failed'));
        }
        finally {
            setCustomMcpSubmitting(false);
        }
    }
    function handleRemoveCustomMcp(meta) {
        if (!meta.mcpUid)
            return;
        Modal.confirm({
            title: ze('确认删除此 MCP Server？', 'Delete this MCP Server?'),
            content: ze(`删除 ${meta.title} 后，Agent 将不再能访问该 MCP 提供的工具。`, `After deleting ${meta.title}, agents will no longer access tools from this MCP.`),
            okText: ze('删除', 'Delete'),
            okButtonProps: { danger: true },
            cancelText: ze('取消', 'Cancel'),
            onOk: async () => {
                await RestHelper.mustPost(`${API_PREFIX}/deleteMcpServer`, { mcpUid: meta.mcpUid });
                message.success(ze('已删除', 'Deleted'));
                fetchConnectors(true);
            },
        });
    }
    // -------------------- 删除（仅快捷接入类） --------------------
    function handleRemove(meta) {
        Modal.confirm({
            title: ze('确认断开此连接器？', 'Disconnect this connector?'),
            content: ze(`断开 ${meta.title} 后，相关数据访问或通知能力将不再可用。`, `After disconnecting ${meta.title}, related data access or notification will stop working.`),
            okText: ze('断开', 'Disconnect'),
            okButtonProps: { danger: true },
            cancelText: ze('取消', 'Cancel'),
            onOk: async () => {
                await RestHelper.mustPost(`${API_PREFIX}/deleteConnector`, { connectorKey: meta.key });
                message.success(ze('已断开', 'Disconnected'));
                fetchConnectors(true);
            },
        });
    }
    // -------------------- 卡片渲染 --------------------
    function renderConnectorCard(meta) {
        const status = meta.mcpUid
            ? (meta.mcpEnabled ? 'connected' : 'disconnected')
            : getStatus(meta.key);
        const isConnected = status === 'connected';
        const isExpired = status === 'expired';
        const isConfigured = isConnected || isExpired;
        const isPolling = oauthPolling.has(meta.key);
        return (_jsxs("div", { className: `${style.connectorCard} ${isConnected ? style.connectorCardConnected
                : isExpired ? style.connectorCardExpired
                    : ''}`, children: [_jsxs("div", { className: style.connectorCardHeader, children: [_jsx("div", { className: style.connectorIcon, children: meta.icon }), _jsxs("div", { className: style.connectorTitleBlock, children: [_jsx("div", { className: style.connectorTitle, children: meta.title }), _jsx("div", { className: style.connectorTypeTag, children: meta.typeLabel })] }), _jsx("div", { className: style.connectorStatus, children: isConnected ? (_jsxs(_Fragment, { children: [_jsx(CheckCircleFilled, { style: { color: '#52c41a', marginRight: 4 } }), _jsx("span", { className: style.connectorStatusText, children: meta.mode === 'oauth' ? ze('已授权', 'Authorized') : ze('可用', 'Active') })] })) : isExpired ? (_jsxs(_Fragment, { children: [_jsx(ExclamationCircleFilled, { style: { color: '#faad14', marginRight: 4 } }), _jsx("span", { className: style.connectorStatusExpired, children: ze('已过期', 'Expired') })] })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: style.connectorStatusDot }), _jsx("span", { className: style.connectorStatusTextMuted, children: ze('未配置', 'Not configured') })] })) })] }), _jsx("div", { className: style.connectorSubtitle, children: meta.subtitle }), _jsxs("div", { className: style.connectorCardFooter, children: [meta.mode === 'auto' && (_jsx(Tooltip, { title: ze('系统自动管理，无需手动操作', 'Auto-managed, no action needed'), children: _jsx(Button, { size: "small", disabled: true, icon: _jsx(CheckCircleFilled, {}), children: ze('自动配置', 'Auto') }) })), meta.mode === 'oauth' && (isConfigured ? (_jsxs(_Fragment, { children: [_jsx(Button, { size: "small", icon: _jsx(ReloadOutlined, {}), onClick: () => handleOAuth(meta.key), children: ze('重新授权', 'Re-authorize') }), _jsx(Button, { size: "small", danger: true, type: "text", onClick: () => handleRemove(meta), children: ze('禁用', 'Disable') })] })) : (_jsx(Button, { size: "small", type: "primary", ghost: true, loading: isPolling, icon: _jsx(LinkOutlined, {}), onClick: () => handleOAuth(meta.key), children: meta.actionText }))), meta.mode === 'qrcode' && (isConnected ? (_jsxs(_Fragment, { children: [_jsx(Button, { size: "small", icon: _jsx(QrcodeOutlined, {}), onClick: openWecomBindModal, children: ze('重新接入', 'Reconnect') }), _jsx(Button, { size: "small", danger: true, type: "text", onClick: () => handleRemove(meta), children: ze('禁用', 'Disable') })] })) : (_jsx(Button, { size: "small", type: "primary", ghost: true, icon: _jsx(QrcodeOutlined, {}), onClick: openWecomBindModal, children: meta.actionText }))), meta.mode === 'form' && (isConnected ? (_jsxs(_Fragment, { children: [_jsx(Button, { size: "small", icon: _jsx(EditOutlined, {}), onClick: () => openFormModal(meta.key), children: ze('编辑', 'Edit') }), _jsx(Button, { size: "small", danger: true, type: "text", onClick: () => handleRemove(meta), children: ze('禁用', 'Disable') })] })) : (_jsx(Button, { size: "small", type: "primary", ghost: true, onClick: () => openFormModal(meta.key), children: meta.actionText }))), meta.mode === 'custom-mcp' && meta.mcpUid && (_jsx(Button, { size: "small", danger: true, type: "text", onClick: () => handleRemoveCustomMcp(meta), children: ze('删除', 'Delete') }))] })] }, meta.key));
    }
    // -------------------- 配置表单（邮件 / Webhook） --------------------
    function renderConfigureForm() {
        if (formModalTarget === 'email') {
            return (_jsxs(Form, { form: form, layout: "vertical", children: [_jsx(Form.Item, { name: "recipients", label: ze('接收邮箱（多个用英文逗号分隔）', 'Recipients (comma separated)'), rules: [{ required: true, message: ze('请输入至少一个邮箱', 'Please enter at least one email') }], children: _jsx(Input.TextArea, { rows: 3, placeholder: "alice@example.com, bob@example.com" }) }), _jsx(Form.Item, { name: "subjectPrefix", label: ze('邮件主题前缀（可选）', 'Subject Prefix (Optional)'), children: _jsx(Input, { placeholder: "[CrashClaw]" }) })] }));
        }
        if (formModalTarget === 'webhook') {
            return (_jsxs(Form, { form: form, layout: "vertical", children: [_jsx(Form.Item, { name: "url", label: "Webhook URL", rules: [{ required: true, type: 'url', message: ze('请输入合法的 URL', 'Please enter a valid URL') }], children: _jsx(Input, { placeholder: "https://example.com/your/webhook" }) }), _jsx(Form.Item, { name: "secret", label: ze('签名密钥（可选）', 'Signing Secret (Optional)'), children: _jsx(Input.Password, { placeholder: ze('用于请求签名校验', 'Used to sign requests') }) })] }));
        }
        return null;
    }
    // -------------------- Main render --------------------
    const defaultConnectors = CONNECTOR_META.filter(m => m.category === 'default');
    const quickConnectors = CONNECTOR_META.filter(m => m.category === 'quick' && !HIDDEN_CONNECTOR_KEYS.has(m.key));
    const totalConnectorCount = quickConnectors.length + defaultConnectors.length + customMcpMetas.length;
    return (_jsxs("div", { className: style.sectionBody, children: [_jsxs("div", { className: style.connectorPanelOpBar, children: [_jsx("div", { className: style.agentCount, children: ze(`共 ${totalConnectorCount} 个连接器`, `${totalConnectorCount} Connectors`) }), _jsxs("div", { style: { display: 'flex', gap: 8 }, children: [_jsx(Button, { icon: _jsx(ReloadOutlined, {}), onClick: () => fetchConnectors(), loading: loading, children: ze('刷新', 'Refresh') }), _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: openCustomMcpModal, children: ze('添加自定义 MCP Server', 'Add Custom MCP Server') })] })] }), _jsxs("div", { className: style.connectorGrid, children: [defaultConnectors.map(renderConnectorCard), quickConnectors.map(renderConnectorCard), customMcpMetas.map(renderConnectorCard)] }), _jsx(Modal, { title: _jsxs("span", { children: [_jsx(WechatOutlined, { style: { color: '#07c160', marginRight: 8 } }), ze('接入企业微信智能机器人', 'Connect WeCom Intelligent Bot')] }), open: wecomBindVisible, onCancel: () => setWecomBindVisible(false), width: 520, footer: null, destroyOnClose: true, children: _jsx(Tabs, { activeKey: wecomBindTab, onChange: (k) => setWecomBindTab(k), items: [
                        {
                            key: 'qrcode',
                            label: ze('扫码绑定', 'Scan & Bind'),
                            children: (_jsxs("div", { className: style.wecomBindBody, children: [_jsx("div", { className: style.wecomBindHint, children: ze('点击「去绑定」，弹出二维码，使用企业微信扫码授权，将自动创建智能机器人并绑定你的助手。', 'Click "Bind Now" to show the QR code. Scan with WeCom to authorize; a bot will be created and bound automatically.') }), _jsx("div", { className: style.wecomBindWarning, children: ze('请注意，绑定完成后将会【自动重启】以使配置生效，立即发起对话可能无响应，请稍等 5-8s 待重启完成后使用。', 'Note: an automatic restart will be performed after binding (~5-8s). Wait briefly before sending the first message.') }), _jsx(Button, { type: "primary", size: "large", icon: _jsx(QrcodeOutlined, {}), className: style.wecomBindCtaBtn, onClick: handleStartWecomQrcodeBind, children: ze('去绑定', 'Bind Now') })] })),
                        },
                        {
                            key: 'manual',
                            label: ze('手动填写', 'Manual Input'),
                            children: (_jsxs("div", { className: style.wecomBindBody, children: [_jsx("div", { className: style.wecomBindSubtitle, children: ze('前置工作', 'Prerequisites') }), _jsxs("div", { className: style.wecomBindHintMuted, children: [ze('创建或使用已有的企微智能机器人（API 模式），选择「长连接」方式。', 'Create or reuse an existing WeCom intelligent bot (API mode), and choose "Long Connection".'), ' ', _jsx("a", { href: "https://work.weixin.qq.com/wework_admin/frame#apps/contactsApi", target: "_blank", rel: "noopener noreferrer", children: ze('查看详细指引', 'View detailed guide') })] }), _jsx("div", { className: style.wecomBindSubtitle, style: { marginTop: 16 }, children: ze('填入配置', 'Configuration') }), _jsx("div", { className: style.wecomBindHintMuted, children: ze('粘贴智能机器人「API 配置 - 长连接」中的 Bot ID 与 Secret 信息', 'Paste the Bot ID and Secret from the bot\'s "API Config - Long Connection" page') }), _jsxs(Form, { form: wecomManualForm, layout: "vertical", style: { marginTop: 12 }, children: [_jsx(Form.Item, { name: "botId", label: "Bot ID", rules: [{ required: true, message: ze('请输入 Bot ID', 'Please enter Bot ID') }], children: _jsx(Input, { placeholder: ze('请输入 Bot ID', 'Enter Bot ID') }) }), _jsx(Form.Item, { name: "secret", label: "Secret", rules: [{ required: true, message: ze('请输入 Secret', 'Please enter Secret') }], children: _jsx(Input.Password, { placeholder: ze('请输入 Secret', 'Enter Secret') }) })] }), _jsxs("div", { className: style.wecomBindManualFooter, children: [_jsx(Button, { type: "primary", loading: wecomManualSubmitting, onClick: handleSubmitWecomManual, children: ze('保存并重启', 'Save & Restart') }), _jsx("span", { className: style.wecomBindManualHint, children: ze('该配置重启后生效，需 5-8s', 'Config takes effect after restart, ~5-8s') })] })] })),
                        },
                    ] }) }), _jsx(Modal, { title: ze('接入企业微信智能机器人', 'Connect WeCom Intelligent Bot'), open: wecomQrModalVisible, onCancel: () => setWecomQrModalVisible(false), width: 400, footer: null, destroyOnClose: true, children: _jsxs("div", { className: style.wecomQrcodeBlock, children: [wecomQrcode ? (_jsx("div", { className: style.wecomQrcodeImgWrap, children: _jsx("img", { src: wecomQrcode, alt: "qrcode", className: style.wecomQrcodeImg }) })) : (_jsx(LoadingOutlined, { style: { fontSize: 48 } })), _jsx("div", { className: style.wecomQrcodeHint, children: wecomStatus === 'success' ? (_jsxs("span", { style: { color: '#52c41a' }, children: [_jsx(CheckCircleFilled, {}), " ", ze('创建成功，正在关闭…', 'Created successfully, closing…')] })) : (_jsxs(_Fragment, { children: [ze('请使用企业微信扫描二维码完成授权配置', 'Scan the QR code with WeCom to complete authorization'), _jsxs("div", { style: { marginTop: 6, fontSize: 11, color: '#c9cdd4' }, children: ["Ticket: ", wecomTicket || '...'] })] })) })] }) }), _jsx(Modal, { title: formModalTarget === 'email' ? ze('配置邮件通知', 'Configure Email') : ze('配置 Webhook', 'Configure Webhook'), open: formModalVisible, onCancel: () => setFormModalVisible(false), onOk: handleFormSubmit, okText: ze('保存', 'Save'), cancelText: ze('取消', 'Cancel'), children: renderConfigureForm() }), _jsxs(Modal, { title: ze('添加 MCP Server', 'Add MCP Server'), open: customMcpVisible, onCancel: closeCustomMcpModal, maskClosable: false, width: 800, destroyOnClose: true, footer: (_jsxs("div", { className: style.modalFooter, children: [_jsx(Button, { onClick: handleCustomMcpTestConnection, loading: customMcpTestLoading, disabled: customMcpSubmitting, children: ze('测试连接', 'Test Connection') }), _jsxs("div", { className: style.modalFooterRight, children: [_jsx(Button, { onClick: closeCustomMcpModal, children: ze('取消', 'Cancel') }), _jsx(Button, { type: "primary", onClick: handleCustomMcpSubmit, loading: customMcpSubmitting, disabled: customMcpTestLoading, children: ze('确定', 'OK') })] })] })), children: [_jsxs(Form, { form: customMcpForm, layout: "vertical", initialValues: { transportType: 'STREAMABLE_HTTP' }, children: [_jsx(Form.Item, { name: "serverName", label: ze('名称', 'Name'), rules: [{ required: true, message: ze('请输入 MCP Server 名称', 'Please enter MCP Server name') }], children: _jsx(Input, { placeholder: ze('例如 my-gitlab-mcp', 'e.g. my-gitlab-mcp') }) }), _jsx(Form.Item, { name: "description", label: ze('描述', 'Description'), children: _jsx(Input, { placeholder: ze('可选，备注描述', 'Optional description') }) }), _jsx(Form.Item, { name: "transportType", label: ze('连接方式', 'Transport Type'), rules: [{ required: true, message: ze('请选择连接方式', 'Please select transport type') }], children: _jsxs(Radio.Group, { children: [_jsx(Radio.Button, { value: "SSE", children: "SSE" }), _jsx(Radio.Button, { value: "STREAMABLE_HTTP", children: "Streamable HTTP" }), _jsx(Radio.Button, { value: "STDIO", children: "STDIO" })] }) }), _jsx(Form.Item, { name: "serverConfig", label: ze('服务配置', 'Server Config'), rules: [{ required: true, message: ze('请输入服务配置 JSON', 'Please enter server config JSON') }], extra: ze('JSON 格式。SSE / Streamable HTTP 需填写 url、headers 等；STDIO 需填写 command、args 等。', 'JSON format. SSE / Streamable HTTP requires url, headers, etc.; STDIO requires command, args, etc.'), children: _jsx(Input.TextArea, { className: style.jsonTextarea, rows: 8, placeholder: `{\n  "url": "https://mcp.example.com/sse",\n  "headers": {\n    "Authorization": "Bearer <token>"\n  }\n}` }) })] }), customMcpTestLoading && (_jsxs("div", { className: style.testResultArea, children: [_jsx(LoadingOutlined, { style: { marginRight: 8 } }), ze('正在测试连通性，请稍候…', 'Testing connectivity, please wait…')] })), !customMcpTestLoading && customMcpTestResult && customMcpTestResult.success && (_jsxs("div", { className: `${style.testResultArea} ${style.testResultSuccess}`, children: [_jsxs("div", { className: style.testResultHeader, children: [_jsx(CheckCircleFilled, { style: { color: '#00B894', marginRight: 8 } }), _jsx("span", { children: ze('连接成功', 'Connection successful') }), _jsxs("span", { className: style.testLatency, children: ["(", customMcpTestResult.latencyMs || 0, "ms)"] })] }), customMcpTestResult.serverName && (_jsxs("div", { className: style.testResultMeta, children: ["Server: ", customMcpTestResult.serverName, customMcpTestResult.serverVersion ? ` v${customMcpTestResult.serverVersion}` : '', customMcpTestResult.protocolVersion ? ` · Protocol ${customMcpTestResult.protocolVersion}` : ''] })), _jsxs("div", { className: style.testResultMeta, children: [ze(`支持 ${customMcpTestResult.tools?.length ?? 0} 个工具`, `${customMcpTestResult.tools?.length ?? 0} tool(s) available`), (customMcpTestResult.tools?.length ?? 0) > 0 && ': ', customMcpTestResult.tools?.map(t => t.name).join(', ')] })] })), !customMcpTestLoading && customMcpTestResult && !customMcpTestResult.success && (_jsxs("div", { className: `${style.testResultArea} ${style.testResultFailure}`, children: [_jsxs("div", { className: style.testResultHeader, children: [_jsx(LoadingOutlined, { style: { display: 'none' } }), _jsx("span", { style: { color: '#E74C3C' }, children: ze('✕ 连接失败', '✕ Connection failed') })] }), customMcpTestResult.errorMessage && (_jsx("div", { className: style.testResultError, children: customMcpTestResult.errorMessage }))] }))] })] }));
};
export default CrashClawConnectorPanel;
